import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";

export default function PartyAgeingReport() {
  const [report, setReport] = useState([]);
  const today = new Date();

  useEffect(() => {
    const fetchData = async () => {
      const { loadSalesImportInvoiceLikeDocs } = await import("../services/salesImportTransactionDataService");
      const invoices = await loadSalesImportInvoiceLikeDocs({ maxWeeks: 24 });

      // 🔹 Fetch vouchers (receipts/payments)
      const vouSnap = await getDocs(collection(db, "vouchers"));
      const vouchers = vouSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // 🔹 Prepare map
      let partyData = {};

      invoices.forEach(inv => {
        const partyId = inv.partyDetails?.id || "unknown";
        const partyName = inv.partyDetails?.partyName || "Unknown";
        const amount = inv.grandTotal || 0;

        const creditDays = inv.creditDays || 0;
        const dueDate = new Date(inv.date);
        dueDate.setDate(dueDate.getDate() + creditDays);

        const ageDays = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
        const bucket = getBucket(ageDays);

        if (!partyData[partyId]) {
          partyData[partyId] = {
            partyName,
            "0-30": 0,
            "31-60": 0,
            "61-90": 0,
            "91-180": 0,
            "180+": 0,
            total: 0,
          };
        }

        // Initially add full invoice amount
        partyData[partyId][bucket] += amount;
        partyData[partyId].total += amount;
      });

      // 🔹 Adjust with vouchers (receipts/payments)
      vouchers.forEach(v => {
        (v.rows || []).forEach(r => {
          const party = r.party || "";
          if (!party) return;

          const amount = Number(r.debit || 0) + Number(r.credit || 0);
          const isReceipt = r.drcr === "Cr"; // customer paid us
          const isPayment = r.drcr === "Dr"; // we paid supplier

          if (!partyData[party]) {
            // ensure entry
            partyData[party] = {
              partyName: party,
              "0-30": 0,
              "31-60": 0,
              "61-90": 0,
              "91-180": 0,
              "180+": 0,
              total: 0,
            };
          }

          // Reduce from total outstanding
          if (isReceipt) {
            // Customer receipt reduces outstanding
            partyData[party].total -= amount;

            // For simplicity, reduce from youngest bucket
            if (partyData[party]["0-30"] >= amount) {
              partyData[party]["0-30"] -= amount;
            } else {
              let bal = amount - partyData[party]["0-30"];
              partyData[party]["0-30"] = 0;

              if (partyData[party]["31-60"] >= bal) {
                partyData[party]["31-60"] -= bal;
              } else {
                bal -= partyData[party]["31-60"];
                partyData[party]["31-60"] = 0;

                if (partyData[party]["61-90"] >= bal) {
                  partyData[party]["61-90"] -= bal;
                } else {
                  bal -= partyData[party]["61-90"];
                  partyData[party]["61-90"] = 0;

                  if (partyData[party]["91-180"] >= bal) {
                    partyData[party]["91-180"] -= bal;
                  } else {
                    bal -= partyData[party]["91-180"];
                    partyData[party]["91-180"] = 0;

                    partyData[party]["180+"] = Math.max(
                      partyData[party]["180+"] - bal,
                      0
                    );
                  }
                }
              }
            }
          }

          if (isPayment) {
            // Suppliers - similar logic (you can adapt here)
            partyData[party].total -= amount;
            partyData[party]["0-30"] = Math.max(
              partyData[party]["0-30"] - amount,
              0
            );
          }
        });
      });

      setReport(Object.values(partyData));
    };

    fetchData();
  }, []);

  function getBucket(days) {
    if (days <= 30) return "0-30";
    if (days <= 60) return "31-60";
    if (days <= 90) return "61-90";
    if (days <= 180) return "91-180";
    return "180+";
  }

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      <MenuBar />
      <HeaderBar title="Party Ageing Report" />

      <div className="p-6 flex-1">
        <div className="bg-white shadow rounded-xl p-4 border">
          <h3 className="text-lg font-semibold mb-4">Outstanding Ageing</h3>
          <table className="w-full border-collapse text-sm">
            <thead className="bg-blue-100">
              <tr>
                <th className="border p-2">Party</th>
                <th className="border p-2 text-right">0-30</th>
                <th className="border p-2 text-right">31-60</th>
                <th className="border p-2 text-right">61-90</th>
                <th className="border p-2 text-right">91-180</th>
                <th className="border p-2 text-right">180+</th>
                <th className="border p-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {report.map(r => (
                <tr key={r.partyName}>
                  <td className="border p-2">{r.partyName}</td>
                  <td className="border p-2 text-right">{r["0-30"].toFixed(2)}</td>
                  <td className="border p-2 text-right">{r["31-60"].toFixed(2)}</td>
                  <td className="border p-2 text-right">{r["61-90"].toFixed(2)}</td>
                  <td className="border p-2 text-right">{r["91-180"].toFixed(2)}</td>
                  <td className="border p-2 text-right">{r["180+"].toFixed(2)}</td>
                  <td className="border p-2 text-right font-semibold">{r.total.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
